import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { User } from "../../services/api";
import { useUsers } from "../../hooks/useUsers";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import { lightTheme } from "../../styles/theme";
import { phoneMatchesSearch } from "../../utils/phoneUtils";
import SearchField from "./SearchField";
import ActionModal from "./ActionModal";

interface DirectNoticeLauncherProps {
  open: boolean;
  onClose: () => void;
  onSelectUser: (user: User, mode: "direct_notice" | "dm_event") => void;
}

const DirectNoticeLauncher: React.FC<DirectNoticeLauncherProps> = ({
  open,
  onClose,
  onSelectUser,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMode, setSelectedMode] = useState<
    "direct_notice" | "dm_event" | null
  >(null);
  const { data: users = [], isLoading, error } = useUsers({ enabled: open });

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }

    const normalizedQuery = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(normalizedQuery) ||
        user.email?.toLowerCase().includes(normalizedQuery) ||
        phoneMatchesSearch(user.phoneNumber, searchQuery),
    );
  }, [searchQuery, users]);

  const handleClose = () => {
    setSearchQuery("");
    setSelectedMode(null);
    onClose();
  };

  const handleSelectUser = (user: User) => {
    if (!selectedMode) return;
    setSearchQuery("");
    setSelectedMode(null);
    onSelectUser(user, selectedMode);
  };

  const messageTypeCards = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(2, minmax(150px, 170px))",
          gap: 1,
        }}
      >
        <Paper
          elevation={0}
          onClick={() => setSelectedMode("direct_notice")}
          sx={{
            p: 1.5,
            border: "1px solid",
            borderColor:
              selectedMode === "direct_notice" ? "primary.main" : "divider",
            borderRadius: 1.5,
            cursor: "pointer",
            aspectRatio: "1 / 1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            bgcolor:
              selectedMode === "direct_notice"
                ? alpha(theme.palette.primary.main, 0.08)
                : "background.paper",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              color: "primary.main",
            }}
          >
            <MailOutlineIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2, textAlign: "center" }}>
            Send DM
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            Private direct notice
          </Typography>
        </Paper>
        <Paper
          elevation={0}
          onClick={() => setSelectedMode("dm_event")}
          sx={{
            p: 1.5,
            border: "1px solid",
            borderColor: selectedMode === "dm_event" ? "primary.main" : "divider",
            borderRadius: 1.5,
            cursor: "pointer",
            aspectRatio: "1 / 1",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            bgcolor:
              selectedMode === "dm_event"
                ? alpha(theme.palette.primary.main, 0.08)
                : "background.paper",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              color: "primary.main",
            }}
          >
            <EventAvailableOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2, textAlign: "center" }}>
            Set up Meeting
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            Private event notice
          </Typography>
        </Paper>
      </Box>
    </Box>
  );

  const listPane = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search people…"
        disabled={!selectedMode}
        size="medium"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 1.75,
            height: 48,
            minHeight: 48,
            bgcolor: "background.paper",
            "& fieldset": { borderColor: alpha(theme.palette.text.primary, 0.12) },
            "&:hover fieldset": {
              borderColor: selectedMode
                ? "primary.main"
                : alpha(theme.palette.text.primary, 0.12),
            },
            "&.Mui-disabled": {
              "& fieldset": {
                borderColor: alpha(theme.palette.text.primary, 0.12),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.text.primary, 0.12),
              },
            },
          },
        }}
      />

      {isLoading && (
        <Box sx={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && error && <Alert severity="error">Failed to load users.</Alert>}

      {!isLoading && !error && filteredUsers.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <PersonIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
            <Typography sx={{ fontWeight: 400, fontSize: "1rem" }}>People</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {filteredUsers.map((item) => (
              <Paper
                key={item.id}
                elevation={0}
                onClick={() => handleSelectUser(item)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  backgroundColor: "background.paper",
                  px: 1.25,
                  py: 1,
                  cursor: selectedMode ? "pointer" : "not-allowed",
                  opacity: selectedMode ? 1 : 0.6,
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: getAvatarColor(item.name, lightTheme),
                    color: "common.white",
                    fontSize: (t) => t.typography.body2.fontSize,
                    fontWeight: 600,
                  }}
                >
                  {getInitials(item.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 400, fontSize: (t) => t.typography.body2.fontSize }}>
                    {item.name}
                  </Typography>
                  <Typography noWrap variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                    {item.email || item.phoneNumber || "No contact details"}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelectUser(item);
                  }}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: "primary.main",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                  }}
                  aria-label={`Send direct message to ${item.name}`}
                  disabled={!selectedMode}
                >
                  <SendIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Paper>
            ))}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <ActionModal
        open={open}
        onClose={handleClose}
        title="Send Direct Message"
        subtitle="Send messages to anyone. Anytime."
        maxWidth={isMobile ? "sm" : "lg"}
        paperSx={!isMobile ? { maxWidth: 1080 } : undefined}
        headerStyle="light"
        dialogContentSx={{
          overflowY: "hidden",
          minHeight: { md: 0 },
        }}
        contentSx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2 },
          pb: { xs: 2.5, sm: 2 },
          minHeight: { md: 520 },
          maxHeight: { md: "calc(95vh - 200px)" },
          display: { md: "flex" },
          flexDirection: { md: "column" },
          overflow: { md: "hidden" },
        }}
      >
        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {messageTypeCards}
            {listPane}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1fr",
              gap: 2,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box sx={{ minHeight: 0, overflowY: "auto", pr: 0.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {messageTypeCards}
                {listPane}
              </Box>
            </Box>
            <Box
              sx={{
                borderLeft: "1px solid",
                borderColor: "divider",
                pl: 2,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflowY: "auto",
                gap: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "text.primary" }}>
                Send messages instantly
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pick a recipient from the list on the left.
              </Typography>
              <Box
                component="img"
                src="/create/create.webp"
                alt="Send messages"
                loading="lazy"
                decoding="async"
                sx={{
                  width: "100%",
                  maxHeight: 160,
                  objectFit: "contain",
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        )}
      </ActionModal>

    </>
  );
};

export default DirectNoticeLauncher;
